import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Carousel } from 'antd';
import { useTranslation } from 'react-i18next';

export default function SponsorsPartners({ pageType, id = '' }: { pageType: string; id?: string }) {
  const { t } = useTranslation();
  // Logos that failed to load fall back to the sponsor name instead of a broken-image icon.
  const [failedLogos, setFailedLogos] = useState<Record<string, boolean>>({});

  // Sponsor listesi yavaş değişen referans verisi. React Query cache'i sayesinde
  // sayfa geçişlerinde ve aynı sayfadaki birden fazla mount'ta tekrar okunmaz.
  // Firestore SDK'sı sayfa altındaki bu bölüm için geç yüklenir; ana sayfanın ilk paketine girmez.
  const { data: activeSponsorAndPartners = [] } = useQuery(
    'sponsorsAndPartners',
    () => import('../../lib/firestoreClient').then(module => module.getSponsorsAndPartners()),
    {
      staleTime: 60 * 60 * 1000,
      cacheTime: 2 * 60 * 60 * 1000,
      refetchOnWindowFocus: false,
    },
  );

  const filter = (type: number, activeSponsorAndPartners: SponsorAndPartner.Item[]) => {
    const filteredArray = activeSponsorAndPartners?.filter(item => item.kayit_tipi === type);
    if (pageType === 'HOME') {
      return filteredArray?.filter(
        item =>
          !item.lig_temel_ids || !item.lig_temel_ids?.length || item.lig_temel_ids.includes('0'),
      );
    } else if (pageType === 'LEAGUE_DETAIL') {
      return filteredArray?.filter(
        item => item.lig_temel_ids?.includes(id) || item.lig_temel_ids?.includes('0'),
      );
    } else if (pageType === 'CLUB_DETAIL') {
      return filteredArray?.filter(
        item => item.klup_ids?.includes(id) || item.klup_ids?.includes('0'),
      );
    } else if (pageType === 'TOURNAMENT_DETAIL') {
      return filteredArray?.filter(
        item => item.turnuva_ids?.includes(id) || item.turnuva_ids?.includes('0'),
      );
    } else {
      return [];
    }
  };

  const sponsors = filter(0, activeSponsorAndPartners);
  const partners = filter(1, activeSponsorAndPartners);

  const content = ({ arr, title }: { arr: SponsorAndPartner.Item[]; title: string }) => {
    let sliders: Array<SponsorAndPartner.Item[]> = [];
    const slideCount = window.innerWidth > 991 ? 4 : window.innerWidth > 767 ? 3 : 2;
    const slideMaxWidth =
      window.innerWidth > 1200 ? 1100 / slideCount : (window.innerWidth - 58) / slideCount;
    arr.forEach((item, index) => {
      if (index % slideCount === 0) {
        sliders.push([item]);
      } else {
        sliders[sliders.length - 1].push(item);
      }
    });

    return sliders.length ? (
      <div className="container">
        <h2 className="sponsor-title">{title}</h2>
        <div className="carousel">
          <Carousel autoplay autoplaySpeed={3000}>
            {sliders.map((slider: SponsorAndPartner.Item[], index) => (
              <div key={index + '-slider'}>
                <div className="sponsor">
                  {slider.map((item: SponsorAndPartner.Item, index) => (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sponsor-item"
                      style={{ maxWidth: slideMaxWidth + 'px' }}
                      key={item.id + index}
                    >
                      {item.foto_url && !failedLogos[item.id] ? (
                        <img
                          src={item.foto_url}
                          alt={item.baslik}
                          width={slideMaxWidth}
                          height={slideMaxWidth}
                          loading="lazy"
                          decoding="async"
                          onError={() => setFailedLogos(prev => ({ ...prev, [item.id]: true }))}
                        />
                      ) : (
                        <span className="sponsor-item-name">{item.baslik}</span>
                      )}
                      {item.aciklama && (
                        <div className="sponsor-item-description">{item.aciklama}</div>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </Carousel>
        </div>
      </div>
    ) : (
      <></>
    );
  };

  return (
    <>
      {sponsors?.length ? content({ arr: sponsors, title: t('sponsors.sponsors') }) : <></>}
      {sponsors.length && partners.length ? <div className="divider" /> : <></>}
      {partners?.length ? content({ arr: partners, title: t('sponsors.partners') }) : <></>}
    </>
  );
}
